import React from "react";
import { Link } from "react-router-dom";
import { Plus, ShoppingBag } from "lucide-react";
import { useCart } from "../../contexts/CartContext";
import { isOutOfStock, kicker, mainImage, pricing, productId } from "./storeTaxonomy";
import "./shop.css";

export const STORE_PHONE = "(08) 8672 5288";
export const STORE_TEL = "tel:+61886725288";

const aud = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });
export const formatAUD = (n) => aud.format(n);

export const productPath = (product) => `/opal-store/product/${productId(product)}`;

/** Price line used on cards: sale with the old price struck through, or Call for Price. */
export function PriceLabel({ product }) {
  const { price, regular, onSale } = pricing(product);
  if (onSale) {
    return (
      <>
        {formatAUD(price)}
        <span className="sh-card__was">{formatAUD(regular)}</span>
      </>
    );
  }
  return price > 0 ? formatAUD(price) : "Call for Price";
}

/** Catalogue grid card: square photo, quick add, kicker, name, price. */
export function ShopProductCard({ product }) {
  const { addToCart } = useCart();
  const { price } = pricing(product);
  const oos = isOutOfStock(product);
  const canQuickAdd = !oos && price > 0;
  const img = mainImage(product);

  return (
    <Link to={productPath(product)} className="sh-card">
      <div className="sh-card__media">
        {img ? <img src={img} alt={product.name} loading="lazy" decoding="async" /> : null}
        {product.featured && <span className="sh-card__badge">FEATURED</span>}
        {canQuickAdd && (
          <button
            type="button"
            className="sh-card__add"
            aria-label={`Add ${product.name} to bag`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              addToCart(product);
            }}
          >
            <Plus size={17} />
          </button>
        )}
      </div>
      <div className="sh-card__body">
        <span className={"sh-card__kicker" + (oos ? " sh-card__kicker--oos" : "")}>
          {oos ? "Out of stock" : kicker(product)}
        </span>
        <span className="sh-card__name">{product.name}</span>
        <span className="sh-card__price">
          <PriceLabel product={product} />
        </span>
      </div>
    </Link>
  );
}

export function SkeletonGrid({ count = 8, className }) {
  return (
    <div className={className}>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="sh-skel" aria-hidden="true">
          <div className="sh-skel__img" />
          <div className="sh-skel__line" />
          <div className="sh-skel__line sh-skel__line--short" />
        </div>
      ))}
    </div>
  );
}

export function StoreMaintenance() {
  return (
    <section className="sh-status">
      <div className="sh-status__icon">
        <ShoppingBag size={36} />
      </div>
      <h1 className="sh-status__title">Store Under Maintenance</h1>
      <p className="sh-status__copy">We're making improvements. Visit our physical store in Coober Pedy!</p>
      <div className="sh-status__btns">
        <Link to="/contact" className="sh-btn sh-btn--accent">
          Contact Us
        </Link>
        <Link to="/" className="sh-btn sh-btn--outline">
          Back to Home
        </Link>
      </div>
    </section>
  );
}
